import { useState } from "react";
import { motion } from "framer-motion";
import { Layers, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import BetControl from "../components/BetControl";
import Confetti from "../components/Confetti";
import CoinRain from "../components/CoinRain";
import { PlayingCard } from "../components/PlayingCard";
import { ShareWin } from "../components/WinShareCard";
import { formatCoins } from "../lib/format";
import { playSpin, playWin, playLose } from "../lib/sound";

export default function AndarBahar() {
  const { t } = useLang();
  const { user, setCoins } = useAuth();
  const [bet, setBet] = useState(100);
  const [side, setSide] = useState("andar");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confetti, setConfetti] = useState(0);
  const [rain, setRain] = useState(0);

  const deal = async () => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    playSpin();
    try {
      const { data } = await api.post("/games/andarbahar", { bet, side });
      const total = (data.andar.length + data.bahar.length);
      const revealMs = Math.min(2400, 350 + total * 180);
      setResult(data);
      setCoins(data.coins);
      setTimeout(() => {
        if (data.win > 0) { setConfetti(Date.now()); setRain(Date.now()); playWin(data.win); toast.success(`${t("youWon")} +${formatCoins(data.win)}!`); }
        else { playLose(); toast(`${t("youLost")} (-${formatCoins(bet)})`); }
      }, revealMs);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTimeout(() => setBusy(false), 500);
    }
  };

  const Pile = ({ label, cards, id, isWin }) => (
    <div className={`flex-1 rounded-xl border p-3 min-h-[130px] ${isWin ? "border-emerald-500/60 bg-emerald-500/10" : "border-white/10 bg-black/30"}`} data-testid={`ab-${id}-pile`}>
      <p className={`text-center text-xs font-bold uppercase mb-2 ${side === id ? "text-amber-300" : "text-slate-400"}`}>
        {label} {side === id && `(${t("yourPick")})`}
      </p>
      <div className="flex flex-wrap gap-1.5 justify-center">
        {(cards || []).map((c, i) => <PlayingCard key={i} card={c} delay={i * 0.18} testid={`ab-${id}-card-${i}`} />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <Confetti fire={confetti} />
      <CoinRain fire={rain} />
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">{t("andarBahar")}</h1>
        <p className="text-sm text-slate-400 mt-1">{t("andarBaharDesc")}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 mw-card p-6 space-y-4" data-testid="ab-table">
          <div className="flex flex-col items-center gap-1">
            <p className="text-xs uppercase tracking-wider text-slate-400">{t("joker")}</p>
            {result ? <PlayingCard card={result.joker} highlight testid="ab-joker" /> :
              <div className="h-24 w-16 rounded-xl border-2 border-dashed border-amber-500/30 grid place-items-center text-amber-500/40"><Layers size={22} /></div>}
          </div>
          <div className="flex gap-3">
            <Pile label={t("andar")} cards={result?.andar} id="andar" isWin={result?.winning === "andar"} />
            <Pile label={t("bahar")} cards={result?.bahar} id="bahar" isWin={result?.winning === "bahar"} />
          </div>
          {result && (
            <motion.p initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="text-center font-display text-2xl font-extrabold" style={{ color: result.won ? "#00E676" : "#FF3366" }} data-testid="ab-result">
              {result.won ? `${t("youWon")} +${formatCoins(result.win)}` : `${t("youLost")} -${formatCoins(bet)}`}
            </motion.p>
          )}
          {result?.won && <div className="flex justify-center"><ShareWin amount={result.win} game="andarbahar" /></div>}
        </div>

        <div className="mw-card p-6 space-y-5">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setSide("andar")} disabled={busy} data-testid="ab-side-andar"
              className={`rounded-xl py-3 font-bold transition-colors ${side === "andar" ? "btn-gold" : "border border-amber-500/30 bg-black/30 text-amber-200"}`}>{t("andar")}</button>
            <button onClick={() => setSide("bahar")} disabled={busy} data-testid="ab-side-bahar"
              className={`rounded-xl py-3 font-bold transition-colors ${side === "bahar" ? "btn-gold" : "border border-amber-500/30 bg-black/30 text-amber-200"}`}>{t("bahar")}</button>
          </div>
          <BetControl bet={bet} setBet={setBet} max={user?.coins} disabled={busy} />
          <div className="rounded-lg border border-amber-500/20 bg-black/30 py-2 text-center text-xs text-slate-400">
            {t("winPays")} <span className="font-mono-num text-amber-300 font-bold">1.95×</span>
          </div>
          <button onClick={deal} disabled={busy} data-testid="ab-deal-btn" className="w-full btn-gold rounded-xl py-3 text-lg flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Layers size={18} />} {t("deal")}
          </button>
        </div>
      </div>
    </div>
  );
}
